/**
 * EditGateBanner
 *
 * Shown on Units, Availability, and Pricing tabs when the offer status is
 * SENT, ACCEPTED, CONFIRMED, or SOLD. Informs the planner that edits will land
 * in the next published version. Does NOT block editing — a sold campaign
 * stays editable too (campaign-sale / "Sold campaign state").
 *
 * A CANCELLED offer instead renders a distinct terminal cue: editing is
 * disabled outright (see workspace-access.ts's `isOfferReadOnly`), there is
 * no publish path, so this is a notice rather than a warning — visually and
 * textually distinct from the "edits create a new version" cue above, and it
 * offers no "view versions" CTA (offer-lifecycle-reversibility /
 * "Edit-Gating UX on Non-DRAFT Offers").
 *
 * Traceability: REQ-7.6 → offer-versioning / Edit-Gating UX on Non-DRAFT Offers
 */
import * as React from 'react';
import { Ban, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '../../plugs/link';
import type { OfferStatus } from '@ooh/contracts';

export interface EditGateBannerProps {
  offerId: string;
  offerStatus: OfferStatus;
}

function getStatusText(
  offerStatus: OfferStatus,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
): string {
  if (offerStatus === 'SENT') return t('workspace.versions.editGateBanner.statusSent');
  if (offerStatus === 'ACCEPTED') return t('workspace.versions.editGateBanner.statusAccepted');
  // Explicit SOLD branch, not folded into the CONFIRMED catch-all below — a sold
  // campaign must show its own status text, not silently reuse the confirmed
  // campaign's wording (campaign-sale / "Sold campaign state").
  if (offerStatus === 'SOLD') return t('workspace.versions.editGateBanner.statusSold');
  return t('workspace.versions.editGateBanner.statusConfirmed');
}

export function EditGateBanner({ offerId, offerStatus }: EditGateBannerProps) {
  const { t } = useTranslation('offers');

  if (offerStatus === 'CANCELLED') {
    return (
      <div
        className="flex items-start gap-3 border-b border-red-200 bg-red-50 px-4 py-3"
        data-testid="cancelled-edit-gating-banner"
      >
        <Ban className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
        <div className="text-sm font-semibold text-red-900">
          {t('workspace.versions.editGateBanner.cancelledTitle')}
        </div>
      </div>
    );
  }

  if (offerStatus === 'DRAFT') {
    return null;
  }

  // Only show for SENT, ACCEPTED, CONFIRMED, SOLD
  if (
    offerStatus !== 'SENT' &&
    offerStatus !== 'ACCEPTED' &&
    offerStatus !== 'CONFIRMED' &&
    offerStatus !== 'SOLD'
  ) {
    return null;
  }

  return (
    <div
      className="flex items-start justify-between gap-4 border-b border-amber-200 bg-amber-50 px-4 py-3"
      data-testid="non-draft-edit-gating-banner"
    >
      <div className="flex items-start gap-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div>
          <div className="text-sm font-semibold text-amber-900">
            {t('workspace.versions.editGateBanner.title')}
          </div>
          <div className="mt-1 text-xs text-amber-800">{getStatusText(offerStatus, t)}</div>
        </div>
      </div>
      <Link
        to={`/offers/${offerId}?tab=versions`}
        className="inline-flex shrink-0 items-center rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium shadow-xs hover:bg-accent hover:text-accent-foreground"
        data-testid="versions-link"
      >
        {t('workspace.versions.editGateBanner.viewVersionsBtn')}
      </Link>
    </div>
  );
}
